import type { CompleteClerkOnboardingData } from '../services/api/auth';

export const PENDING_CLERK_ONBOARDING_STORAGE_VERSION = 2 as const;

/** Opaque handoff pointer stored client-side after JWT is available (see POST /onboarding/clerk-handoff). */
export type PendingClerkOnboardingHandoffEnvelope = {
  v: typeof PENDING_CLERK_ONBOARDING_STORAGE_VERSION;
  handoff_token: string;
  role: CompleteClerkOnboardingData['role'];
  isSelfManagingOwner: boolean;
  /** When true, client must collect Plaid before POST complete (PM non–self-managing). */
  requires_pm_plaid?: boolean;
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isValidRole(r: unknown): r is CompleteClerkOnboardingData['role'] {
  return (
    r === 'PROPERTY_MANAGER' ||
    r === 'OWNER' ||
    r === 'VENDOR' ||
    r === 'INVESTOR' ||
    r === 'TENANT'
  );
}

export function isPendingClerkOnboardingHandoffEnvelope(v: unknown): v is PendingClerkOnboardingHandoffEnvelope {
  if (!isRecord(v)) return false;
  if (v.v !== PENDING_CLERK_ONBOARDING_STORAGE_VERSION) return false;
  if (typeof v.handoff_token !== 'string' || !v.handoff_token.trim()) return false;
  if (!isValidRole(v.role)) return false;
  if (typeof v.isSelfManagingOwner !== 'boolean') return false;
  if (v.requires_pm_plaid !== undefined && typeof v.requires_pm_plaid !== 'boolean') return false;
  return true;
}

/** Map envelope or legacy full payload into the shape used by complete-onboarding (handoff merge supplies the rest). */
/** Whether complete-onboarding must show Plaid before POST (client gate; server enforces too). */
export function pendingPayloadNeedsPmPlaid(parsedRoot: unknown, data: CompleteClerkOnboardingData): boolean {
  if (isPendingClerkOnboardingHandoffEnvelope(parsedRoot)) {
    if (parsedRoot.requires_pm_plaid === true) return true;
    if (parsedRoot.requires_pm_plaid === false) return false;
    return data.role === 'PROPERTY_MANAGER' && data.isSelfManagingOwner !== true;
  }
  return (
    data.role === 'PROPERTY_MANAGER' &&
    data.isSelfManagingOwner !== true &&
    !data.trustAccountingPlaid?.operating?.processorToken?.trim()
  );
}

export function pendingStorageToCompletionPayload(
  parsed: unknown,
): CompleteClerkOnboardingData | null {
  if (isPendingClerkOnboardingHandoffEnvelope(parsed)) {
    return {
      role: parsed.role,
      isSelfManagingOwner: parsed.isSelfManagingOwner,
      handoff_token: parsed.handoff_token.trim(),
    };
  }
  if (!isRecord(parsed)) return null;
  if (!isValidRole(parsed.role)) return null;
  if (typeof parsed.isSelfManagingOwner !== 'boolean') return null;
  return parsed as unknown as CompleteClerkOnboardingData;
}
